import { Button, Input, Link, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';

export interface FailedSignupIdentifier {
  id: number;
  correctedValue: string;
  originalValue: string;
  error: string;
  input: string;
  isEditing: boolean;
  isResolved: boolean;
  isValidating: boolean;
}

export interface ValidSignupUser {
  name: string;
  alias: string;
  shiftName: string;
}

export interface AlreadySignedUpUser {
  name: string;
  alias: string;
  shiftName: string;
}

export function useInvalidSignupColumns(
  onCorrectedValueChange: (id: number, value: string) => void,
  onAcceptCorrection: (id: number) => void,
  onCancelEdit: (id: number) => void,
  onEditItem: (id: number) => void
) {
  const { t } = useTranslation('signup');

  return [
    {
      id: 'input',
      header: t('bulkSignupModal.validation.tableHeaders.yourInput'),
      cell: (item: FailedSignupIdentifier) => item.input,
    },
    {
      id: 'error',
      header: t('bulkSignupModal.validation.tableHeaders.validationError'),
      cell: (item: FailedSignupIdentifier) => (
        <StatusIndicator type={item.isResolved ? 'success' : 'error'}>{item.error}</StatusIndicator>
      ),
    },
    {
      id: 'corrected',
      header: t('bulkSignupModal.validation.tableHeaders.correctedValue'),
      cell: (item: FailedSignupIdentifier) => (
        <SpaceBetween alignItems="center" direction="horizontal" size="xs">
          <Input
            value={item.correctedValue}
            onChange={({ detail }) => onCorrectedValueChange(item.id, detail.value)}
            readOnly={!item.isEditing || item.isResolved}
          />
          {!item.isEditing && !item.isResolved && (
            <Button
              iconName="edit"
              variant="inline-icon"
              onClick={() => onEditItem(item.id)}
              ariaLabel={t('bulkSignupModal.validation.ariaLabels.editItem', { input: item.input })}
            />
          )}
          {item.isEditing && !item.isResolved && (
            <>
              <Button
                iconName="close"
                variant="inline-icon"
                onClick={() => onCancelEdit(item.id)}
                disabled={item.isValidating}
                ariaLabel={t('bulkSignupModal.validation.ariaLabels.cancelEdit', {
                  input: item.input,
                })}
              />
              <Button
                iconName="check"
                variant="inline-icon"
                onClick={() => onAcceptCorrection(item.id)}
                loading={item.isValidating}
                disabled={item.isValidating}
                ariaLabel={t('bulkSignupModal.validation.ariaLabels.acceptCorrection', {
                  input: item.input,
                })}
              />
            </>
          )}
        </SpaceBetween>
      ),
    },
  ];
}

export function useValidSignupColumns() {
  const { t } = useTranslation('signup');

  return [
    {
      id: 'name',
      header: t('bulkSignupModal.validation.tableColumns.name'),
      cell: (item: ValidSignupUser) => item.name,
    },
    {
      id: 'alias',
      header: t('bulkSignupModal.validation.tableColumns.alias'),
      cell: (item: ValidSignupUser) => (
        <Link
          target="_blank"
          href={`https://phonetool.amazon.com/users/${item.alias}`}
          variant="primary"
        >
          {item.alias}
        </Link>
      ),
    },
    {
      id: 'shiftName',
      header: t('bulkSignupModal.validation.tableColumns.shiftName'),
      cell: (item: ValidSignupUser) => item.shiftName,
    },
  ];
}

export function useAlreadySignedUpColumns() {
  const { t } = useTranslation('signup');

  return [
    {
      id: 'name',
      header: t('bulkSignupModal.validation.tableColumns.name'),
      cell: (item: AlreadySignedUpUser) => item.name,
    },
    {
      id: 'alias',
      header: t('bulkSignupModal.validation.tableColumns.alias'),
      cell: (item: AlreadySignedUpUser) => (
        <Link
          target="_blank"
          href={`https://phonetool.amazon.com/users/${item.alias}`}
          variant="primary"
        >
          {item.alias}
        </Link>
      ),
    },
    {
      id: 'shiftName',
      header: t('bulkSignupModal.validation.tableColumns.shiftName'),
      cell: (item: AlreadySignedUpUser) => item.shiftName,
    },
  ];
}
